// Pure presentation + error-mapping logic for the Team Management page
// (pages/TeamManagement.tsx). Kept DB/React-free so it is unit-testable with
// `node --test src/lib/teamView.test.ts` (matching reviewQueue.ts). The page is
// a thin renderer over these helpers, so the load-bearing rules — status/role
// labels, which capabilities an admin may NOT touch, and how server error codes
// become human copy — are proven here, not in a browser.

export type MemberStatus = 'invited' | 'active' | 'deactivated'
export type Role = 'owner' | 'admin' | 'manager' | 'analyst' | 'viewer'

// Mirrors GET /api/v1/team/members rows.
export interface TeamMember {
  id: string
  clerk_user_id: string | null
  name: string
  email: string | null
  status: MemberStatus
  role: Role
  all_locations: boolean
  location_ids: string[]
  permission_overrides: Record<string, boolean>
  active: boolean
  invited_email: string | null
}

export type Capability =
  | 'manage_tracks'
  | 'manage_blocks'
  | 'view_billing'
  | 'manage_billing'
  | 'view_admin'
  | 'manage_admin'
  | 'manage_team'

export const CAPABILITIES: readonly Capability[] = [
  'manage_tracks',
  'view_billing',
  'manage_billing',
  'view_admin',
  'manage_admin',
  'manage_team',
]

// The three levers only an OWNER may grant/revoke (kept in sync with the backend
// team.service OWNER_ONLY_CAPS). Used to disable those toggles for admins.
export const OWNER_ONLY_CAPS: readonly Capability[] = ['manage_billing', 'manage_admin', 'manage_team']

export const CAPABILITY_LABELS: Record<Capability, string> = {
  manage_tracks: 'Manage tracked rivals',
  manage_blocks: 'Manage blocks',
  view_billing: 'View billing',
  manage_billing: 'Manage billing',
  view_admin: 'View admin tools',
  manage_admin: 'Manage admin tools',
  manage_team: 'Manage team',
}

export const ROLES: readonly Role[] = ['owner', 'admin', 'manager', 'analyst', 'viewer']
export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  analyst: 'Analyst',
  viewer: 'Viewer',
}

// Status → label + Pill tone (uses the existing design tokens/tones).
export type Tone = 'success' | 'warning' | 'danger' | 'neutral'
export function statusMeta(status: MemberStatus): { label: string; tone: Tone } {
  switch (status) {
    case 'active':
      return { label: 'Active', tone: 'success' }
    case 'invited':
      return { label: 'Pending invite', tone: 'warning' }
    case 'deactivated':
      return { label: 'Deactivated', tone: 'danger' }
  }
}

export function roleTone(role: Role): Tone {
  if (role === 'owner') return 'danger'
  if (role === 'admin') return 'warning'
  return 'neutral'
}

/**
 * Can the ACTING user edit this capability toggle for a member? An admin cannot
 * touch the owner-only levers (billing/admin/team). Owners can edit everything.
 * The backend enforces this too — this only keeps the UI honest (disabled +
 * explained), never the security boundary.
 */
export function canEditCapability(actorIsOwner: boolean, cap: Capability): boolean {
  if (actorIsOwner) return true
  return !OWNER_ONLY_CAPS.includes(cap)
}

/** Can the acting user set a member's role to `role`? Only owners may grant owner. */
export function canAssignRole(actorIsOwner: boolean, role: Role): boolean {
  if (role === 'owner') return actorIsOwner
  return true
}

// Roster grouped for display: pending invites, active members, deactivated.
export interface RosterGroups {
  pending: TeamMember[]
  active: TeamMember[]
  deactivated: TeamMember[]
}
export function groupRoster(members: TeamMember[]): RosterGroups {
  const g: RosterGroups = { pending: [], active: [], deactivated: [] }
  for (const m of members) {
    if (m.status === 'invited') g.pending.push(m)
    else if (m.status === 'deactivated') g.deactivated.push(m)
    else g.active.push(m)
  }
  return g
}

export function locationScopeLabel(m: TeamMember, totalLocations?: number): string {
  if (m.all_locations || m.role === 'owner' || m.role === 'admin') return 'All locations'
  const n = m.location_ids.length
  if (n === 0) return 'No locations'
  if (totalLocations && n >= totalLocations) return 'All locations'
  return n === 1 ? '1 location' : `${n} locations`
}

/**
 * Map a server response ({ error, code } + HTTP status) to clear, specific UI
 * copy. Every distinguishable failure the task calls out — Clerk config, invite
 * failure, last-owner protection, authorization — gets its own message; unknown
 * codes fall back to the server's own error text, never a generic string.
 */
export interface ServerError {
  status?: number
  code?: string
  error?: string
}
export function mapTeamError(e: ServerError): string {
  switch (e.code) {
    case 'CLERK_ORG_REQUIRED':
      return 'This account isn’t connected to a Clerk Organization yet, so invitations can’t be sent. Ask your administrator to enable Organizations in Clerk and link this account.'
    case 'CLERK_INVITE_FAILED':
      return e.error || 'Clerk couldn’t send the invitation. Please try again shortly.'
    case 'LAST_OWNER':
      return 'You can’t remove or demote the last active owner. Promote another owner first, then retry.'
    case 'OWNER_REQUIRED':
      return e.error || 'Only an owner can change billing, admin, team-management permissions, or the owner role.'
    case 'INVITE_EXISTS':
      return e.error || 'That email already has a pending invitation.'
    case 'MEMBER_EXISTS':
      return 'That user is already a member of this organization.'
    case 'LOCATION_NOT_IN_ORG':
      return 'One or more selected locations don’t belong to this organization.'
    case 'INVALID_PERMISSION_KEY':
    case 'INVALID_PERMISSION_VALUE':
    case 'INVALID_ROLE':
      return e.error || 'That role or permission value isn’t valid.'
    case 'FORBIDDEN':
    case 'AUTH_REQUIRED':
      return 'You don’t have access to team management. Contact an owner or admin.'
    case 'MEMBER_NOT_FOUND':
      return 'That member no longer exists in this organization.'
    default:
      if (e.status === 403) return 'You don’t have access to team management. Contact an owner or admin.'
      if (e.status === 401) return 'Your session expired. Please sign in again.'
      return e.error || 'Something on our side failed. Please try again.'
  }
}
